import React, { useState } from "react";
import Badge from "../common/Badge";
import { isSupernovaTabEnabled } from "../../config/supernovaFlags";
import useSupernovaChain from "./useSupernovaChain";
import useSupernovaWallet from "./useSupernovaWallet";
import summarizeSupernova, { METRIC } from "./supernovaSummary";
import SupernovaStatStrip from "./SupernovaStatStrip";
import SupernovaGateExplainer from "./SupernovaGateExplainer";
import SupernovaBackdrop from "./SupernovaBackdrop";
import SupernovaHero from "./SupernovaHero";
import SupernovaSectionLabel from "./SupernovaSectionLabel";
import SupernovaSlotRail from "./SupernovaSlotRail";
import useAutomationSlots from "./useAutomationSlots";
import { deriveSlots } from "./supernovaSlots";
import { goToStrategyAutomationTab } from "./supernovaNavigation";
import SupernovaCollectionList from "./SupernovaCollectionList";
import SupernovaStakeModal from "./SupernovaStakeModal";
import SupernovaStakeTable from "./SupernovaStakeTable";
import SupernovaUnstakeModal from "./SupernovaUnstakeModal";
import SupernovaTierTrack from "./SupernovaTierTrack";
import SupernovaActivityFeed from "./SupernovaActivityFeed";
import SupernovaAcquire from "./SupernovaAcquire";
import { buildXpBalance } from "./supernovaXpBalance";
import { buildTierTrack } from "./supernovaTiers";
import { buildActivity } from "./supernovaActivity";
import SupernovaToast, { useSupernovaToast } from "./SupernovaToast";
import { DisclosureFooter } from "./SupernovaDisclosure";
import { fmtCount, fmtDate, fmtXpExact, plural, shortAddr } from "./supernovaFormat";
import {
  ChainErrorCard,
  CollectionShowcase,
  CollectionSkeleton,
  ConnectWalletCard,
  EmptyCollectionCard,
  WrongNetworkNotice,
} from "./SupernovaStates";
import "./supernova.css";

/**
 * Supernova tab — the member's read view of their Supernova collection, their
 * stake, and the strategy slots that stake unlocks.
 *
 * FEATURE FLAG (default OFF — invisible to live members):
 *   REACT_APP_SUPERNOVA_TAB_ENABLED=true npm start
 * Off ⇒ this component renders null before any hook mounts, so there is no
 * wallet access, no RPC call, and no network traffic of any kind. Flag off is
 * indistinguishable from today's app.
 *
 * NON-CUSTODIAL. Nothing on this tab signs and nothing on it can move an NFT.
 * Staking and unstaking are transactions the member signs in their own wallet;
 * the Stake and Unstake buttons open modals that quote the model and then hand
 * off to that wallet. ARCrypto holds no key and cannot sign for anyone.
 *
 * WHAT IS REAL vs. WHAT IS A PLACEHOLDER:
 *   REAL         owned counts — ERC-1155 balanceOfBatch on Arbitrum — and the
 *                staked counts and active-stakes table, read from the staking
 *                contract itself (getUserStakes + stakes, supernovaStakeRead)
 *   DERIVED      XP accrual, tier progress and the activity feed — computed from
 *                the stake lots by config/supernovaXp, so they are as real as
 *                the lots underneath them and unavailable when those are
 *   PLACEHOLDER  strategy-slot usage, the BANKED half of XP
 *                (supernovaXpBalance.BANKED_BASE_XP), tier awards, unstake
 *                history, and the acquire path. Each is labelled on screen as
 *                not connected yet and shows an em dash, never a zero. See
 *                supernovaSummary.js and supernovaXpBalance.js for the reasoning
 *                and the TODO(backend, supervised) markers.
 *
 * STAKE STATE IS NOT AN ENTITLEMENT. Everything here is derived from the
 * member's wallet and the chain. Nothing on this tab reads, writes, or is
 * coupled to a Firebase membership record.
 *
 * @param {Object} props
 * @param {(tokenId:number)=>void} [props.onStake]  override for who handles a
 *        Stake click. Absent ⇒ this tab opens its own stake modal. Supplied ⇒
 *        the caller owns the flow and the built-in modal never mounts.
 * @param {(lotId:string)=>void} [props.onUnstake]  the same override for the
 *        Unstake buttons in the Active stakes table.
 * @param {number|null} [props.slotsUsed]           strategies currently automated.
 *        Omitted entirely ⇒ this tab reads slot usage itself, read-only, from
 *        the automation engine (useAutomationSlots). Passed ⇒ the caller's
 *        number wins and no read happens on its behalf.
 * @param {string} [props.slotsUsedStatus]          METRIC status for slotsUsed
 * @param {number|null} [props.xpBalance]           the staking engine's COMPLETE
 *        XP total (banked + accruing). Omitted ⇒ the tab shows accrual on the
 *        open lots instead, labelled "XP accrued" rather than "XP balance".
 * @param {string} [props.xpBalanceStatus]          METRIC status for xpBalance
 * @param {Object} [props.automationService]        injectable signalsApi (tests)
 */
const SupernovaTab = (props) => {
  // Read through the function, not the module-load constant, so a preview build
  // (or a test) that flips the env var is honoured. No hook has run yet.
  if (!isSupernovaTabEnabled()) return null;
  return <SupernovaSurface {...props} />;
};

const SupernovaSurface = ({
  onStake,
  onUnstake,
  slotsUsed,
  slotsUsedStatus,
  xpBalance = null,
  xpBalanceStatus = METRIC.UNAVAILABLE,
  automationService,
}) => {
  const wallet = useSupernovaWallet({ enabled: true });

  /* Hold the read until we have an address AND know we're on Arbitrum: with an
     injected provider the read is routed through it, so a member on another
     network would otherwise see another chain's answer as fact. */
  const readEnabled = Boolean(wallet.account) && wallet.chainOk;
  const chain = useSupernovaChain(wallet.account, { enabled: readEnabled });

  /* ── slot usage ────────────────────────────────────────────────────────
     The other half of "one staked Supernova = one strategy slot" belongs to the
     automation engine, not to this tab. A caller may pass it in (the documented
     prop contract); with the props omitted we read it ourselves — read-only,
     display-only, and unavailable rather than zero when that backend isn't
     configured. Stake state itself stays entirely on-chain either way. */
  const usageFromProps = slotsUsed !== undefined || slotsUsedStatus !== undefined;
  const automation = useAutomationSlots({
    enabled: !usageFromProps, // caller supplied it ⇒ we make no request at all
    service: automationService,
  });
  const slotsUsedValue = usageFromProps ? slotsUsed ?? null : automation.used;
  const slotsUsedStatusValue = usageFromProps
    ? slotsUsedStatus ?? METRIC.UNAVAILABLE
    : automation.status;
  const automatedStrategies = usageFromProps ? null : automation.strategies;

  const summary = summarizeSupernova({
    owned: chain.owned,
    ownedStatus: chain.ownedStatus,
    ownedError: chain.ownedError,
    staked: chain.staked,
    stakedStatus: chain.stakedStatus,
    stakedError: chain.stakedError,
    slotsUsed: slotsUsedValue,
    slotsUsedStatus: slotsUsedStatusValue,
    xpBalance,
    xpBalanceStatus,
  });

  /* ── XP, tiers and activity ───────────────────────────────────────────────
     All three are derived from the SAME lots the Active stakes table renders,
     via the same pure modules, so no two surfaces on this tab can describe one
     stake differently. Each carries its own status: with the stake read
     unconnected they say so rather than showing zeros.

     XP is the one to read carefully, because it has two halves. `xpBalance`/
     `xpBalanceStatus` (the props) are the staking engine's own total and win
     outright, since that single figure already contains both. Absent them, the
     halves are assembled here: ACCRUING from the open lots, and BANKED from the
     closed ones that `chain.closed` rebuilt out of the contract's event log.

     The closed read is a log scan and is slower than the storage reads, so it
     is deliberately NOT part of the tab's loading gate. The tile shows the
     accruing half first — labelled "XP accrued", with the banked half stated as
     missing — and upgrades itself to "XP balance" when the scan lands. At no
     point does it show a figure it can't account for. */
  /* The hook's status strings ARE the METRIC vocabulary — same five values, by
     design (see supernovaSummary's header). No mapping, just a floor. */
  const closedStatus = chain.closedStatus || METRIC.UNAVAILABLE;
  const xpView = buildXpBalance({
    staked: chain.staked,
    stakedStatus: summary.staked.status,
    stakedNote: summary.staked.note,
    stakedMessage: summary.staked.message,
    closed: chain.closed,
    closedStatus,
    engineXp: xpBalance,
    engineStatus: xpBalanceStatus,
  });

  const tierTrack = buildTierTrack(
    summary.staked.status === METRIC.READY ? summary.staked.value : null
  );

  const activity = buildActivity({
    staked: chain.staked,
    stakedStatus: summary.staked.status,
    stakedNote: summary.staked.note,
    stakedMessage: summary.staked.message,
  });

  const slots = deriveSlots({
    stakedCount: summary.staked.status === METRIC.READY ? summary.staked.value : null,
    strategies: slotsUsedStatusValue === METRIC.READY ? automatedStrategies : null,
    usedCount: slotsUsedStatusValue === METRIC.READY ? slotsUsedValue : null,
  });

  const wrongNetwork = wallet.hasInjected && wallet.chainKnown && !wallet.chainOk;
  const { headline } = summary;

  /* ── the stake flow ────────────────────────────────────────────────────
     The modal is mounted only while it is open, so with no Stake click there
     is no approval read and no extra network traffic. A caller that passes
     `onStake` owns the flow instead and the built-in modal stays out of it. */
  const [stakeTokenId, setStakeTokenId] = useState(null);
  const { toast, showToast, dismissToast } = useSupernovaToast();
  const openStake = onStake || setStakeTokenId;

  /* The rail's ghost cell isn't tied to a type, so it opens the modal on the
     first type the member actually holds (the modal lets them switch). With
     nothing stakeable — no wallet, an unread balance, or an empty one — the
     cell stays disabled and says why, rather than opening a modal that can only
     refuse. */
  const railStakeToken = summary.tokens.find((t) => t.stakeable) || null;
  const railStakeTokenId = railStakeToken ? railStakeToken.id : null;
  const railStakeReason = !wallet.account
    ? "Connect your wallet to stake."
    : wrongNetwork
    ? "Switch your wallet to Arbitrum One to stake."
    : summary.owned.status !== METRIC.READY
    ? "We haven't been able to read your collection yet."
    : railStakeToken
    ? undefined
    : "No Supernovas in your wallet to stake. Staked ones are already held by the staking contract.";

  const closeStake = (info) => {
    setStakeTokenId(null);
    /* Anything that reached a signature may have moved NFTs, so re-read even
       when the member closed before we saw a confirmation. */
    if (info?.hash) chain.refresh();
  };

  const handleStaked = (result) => {
    setStakeTokenId(null);
    chain.refresh();
    showToast(
      `Stake confirmed — ${fmtCount(result.qty)} ${result.tokenName} locked for ` +
        `${result.lockLabel}, until ${fmtDate(result.unlockAtMs)}. XP accrues daily ` +
        `from here, up to ${fmtXpExact(result.xpAtMaturity)} XP at full maturity. ` +
        `${result.slots === 1 ? "1 strategy slot" : `${fmtCount(result.slots)} strategy slots`} unlocked — ` +
        "allocate on the Strategy Automation tab."
    );
  };

  /* ── the unstake flow ──────────────────────────────────────────────────
     Same shape as the stake flow: the modal mounts only while it is open, and
     a caller that passes `onUnstake` owns the flow instead. We hold the lot ID
     rather than the lot object so a refresh mid-flow can't leave the modal
     rendering a position that no longer exists. */
  const [unstakeLotId, setUnstakeLotId] = useState(null);
  const openUnstake = onUnstake || setUnstakeLotId;
  const unstakeLot =
    unstakeLotId === null
      ? null
      : (chain.staked?.lots || []).find((l) => l.lotId === unstakeLotId) || null;

  const closeUnstake = (info) => {
    setUnstakeLotId(null);
    /* Anything that reached a signature may have moved NFTs, so re-read even
       when the member closed before we saw a confirmation. */
    if (info?.hash) chain.refresh();
  };

  const handleUnstaked = (result) => {
    setUnstakeLotId(null);
    chain.refresh();
    showToast(
      `Unstake confirmed — ${fmtCount(result.qty)} ${result.tokenName} on their way ` +
        `back to your wallet. ` +
        (result.matured
          ? `${fmtXpExact(result.keptXp)} XP kept from a lock held to maturity. `
          : `Early exit — the model keeps ${fmtXpExact(result.keptXp)} XP after the ` +
            "re-rate and the decay penalty; the contract settles the final figure. ") +
        "The strategy slots this stake backed close with it — review your " +
        "allocations on the Strategy Automation tab."
    );
  };

  return (
    <>
      {/* `.sn-root` is the cosmic surface AND the scope of the v4 token
          override — see supernova.css. Everything the tab paints lives inside
          it; the modals and the toast deliberately do not, so they keep the
          app's own chrome instead of being re-themed by it. */}
      <div className="sn-root" data-testid="supernova-tab">
        <SupernovaBackdrop />

        <div className="sn-wrap">
          {/* ── hero ─────────────────────────────────────────────────────
              The CTA is the same stake modal every other Stake control on this
              tab opens — the member's own wallet signs it. */}
          <SupernovaHero
            onStake={railStakeTokenId === null ? null : () => openStake(railStakeTokenId)}
            stakeReason={railStakeReason}
          >
            {wallet.account && (
              <p className="text-caption text-arc-ink-4 tnum m-0">
                Reading {shortAddr(wallet.account)} on Arbitrum One · read-only
              </p>
            )}
            {headline.status === METRIC.READY ? (
              <Badge
                tone="accent"
                dot
                className="text-[12.5px] px-3.5 py-1.5 rounded-full tnum whitespace-nowrap"
              >
                {plural(headline.stakedCount, "staked", "staked")} →{" "}
                {plural(headline.slotCount, "strategy slot", "strategy slots")}
              </Badge>
            ) : (
              <Badge
                tone="mock"
                className="text-[12.5px] px-3.5 py-1.5 rounded-full whitespace-nowrap"
                title={headline.note}
              >
                Staked count not connected yet
              </Badge>
            )}
          </SupernovaHero>

          {wrongNetwork && (
            <div className="sn-section">
              <WrongNetworkNotice
                onSwitch={wallet.switchToArbitrum}
                switching={wallet.switching}
              />
            </div>
          )}

          {/* ── stat strip ─────────────────────────────────────────────── */}
          <SupernovaStatStrip summary={summary} xp={xpView} />

          {/* ── the collection, or whichever state we're actually in ───── */}
          <section className="sn-section">
            <SupernovaSectionLabel>Your collection</SupernovaSectionLabel>
            <CollectionBody
              summary={summary}
              chain={chain}
              wallet={wallet}
              wrongNetwork={wrongNetwork}
              onStake={openStake}
            />
          </section>

          {/* ── the slot rail ────────────────────────────────────────────
              One cell per slot the stake has lit, labelled with the strategy
              using it. The ghost cell opens the same stake modal. */}
          <section className="sn-section">
            <SupernovaSectionLabel>
              Strategy slots · lit by your stake
            </SupernovaSectionLabel>
            <SupernovaSlotRail
              slots={slots}
              stakedStatus={summary.staked.status}
              stakedNote={summary.staked.note || summary.staked.message}
              usageNote={
                usageFromProps
                  ? summary.slots.note
                  : automation.note || automation.message
              }
              onStake={railStakeTokenId === null ? null : () => openStake(railStakeTokenId)}
              stakeReason={railStakeReason}
              onOpenAutomation={
                slotsUsedStatusValue === METRIC.READY ? goToStrategyAutomationTab : null
              }
            />
          </section>

          {/* ── active stakes ────────────────────────────────────────────
              Hidden entirely without a readable wallet on Arbitrum: the
              collection area above already carries the connect / wrong-network
              state, and a second copy of it would say nothing new. */}
          {wallet.account && !wrongNetwork && (
            <section className="sn-section">
              <SupernovaSectionLabel>Active stakes</SupernovaSectionLabel>
              <SupernovaStakeTable
                staked={chain.staked}
                status={summary.staked.status}
                note={summary.staked.note}
                message={summary.staked.message}
                onUnstake={openUnstake}
                onRetry={chain.refresh}
                retrying={chain.loading}
              />
            </section>
          )}

          {/* ── XP & tier progress ───────────────────────────────────────
              Display only, and no home in the mockup — kept, in a v4 section
              of its own. The tiers come from config; nothing on this tab
              awards or credits one — see SupernovaTierTrack. */}
          <section className="sn-section">
            <SupernovaSectionLabel>XP &amp; tier progress</SupernovaSectionLabel>
            <SupernovaTierTrack
              track={tierTrack}
              stakedStatus={summary.staked.status}
              stakedNote={summary.staked.note || summary.staked.message}
            />
          </section>

          {/* ── recent activity ──────────────────────────────────────────
              Hidden without a readable wallet on Arbitrum, for the same reason
              the stakes table is. Also has no mockup home; also kept. */}
          {wallet.account && !wrongNetwork && (
            <section className="sn-section">
              <SupernovaSectionLabel>Recent activity</SupernovaSectionLabel>
              <SupernovaActivityFeed
                activity={activity}
                onRetry={chain.refresh}
                retrying={chain.loading}
              />
            </section>
          )}

          {/* ── the mechanics, collapsed ─────────────────────────────────
              Reference, not news: the member who wants the model opens it. */}
          <details className="sn-details">
            <summary>How staking works</summary>
            <SupernovaGateExplainer />
          </details>

          {/* ── the acquire seam ─────────────────────────────────────────
              Behind SUPERNOVA_BUY_ENABLED (default OFF) and carrying no
              purchase path of any kind. Shown even without a wallet: "how do I
              get one" is the question a member with an empty wallet has. */}
          <div className="sn-section">
            <SupernovaAcquire />
          </div>

          <DisclosureFooter />
        </div>
      </div>

      {stakeTokenId !== null && (
        <SupernovaStakeModal
          open
          tokenId={stakeTokenId}
          tokens={summary.tokens}
          wallet={wallet.account}
          onClose={closeStake}
          onStaked={handleStaked}
        />
      )}

      {unstakeLot && (
        <SupernovaUnstakeModal
          open
          lot={unstakeLot}
          wallet={wallet.account}
          onClose={closeUnstake}
          onUnstaked={handleUnstaked}
        />
      )}

      <SupernovaToast toast={toast} onDismiss={dismissToast} />
    </>
  );
};

/**
 * The one place that decides which state the collection area is in. Order
 * matters: "no wallet" outranks "no data", and "we couldn't read it" outranks
 * "you have none" — otherwise a failed read would render as an empty wallet.
 */
const CollectionBody = ({ summary, chain, wallet, wrongNetwork, onStake }) => {
  if (!wallet.account) {
    /* The connect card, then the collection itself as a showcase: the four
       types exist whether or not anyone is connected, and showing them costs no
       read. Counts stay off it — see CollectionShowcase. */
    return (
      <div className="space-y-5">
        <ConnectWalletCard
          onConnect={wallet.connect}
          connecting={wallet.connecting}
          hasInjected={wallet.hasInjected}
          error={wallet.error}
        />
        <CollectionShowcase />
      </div>
    );
  }

  if (wrongNetwork) {
    return (
      <ChainErrorCard
        title="Paused — your wallet is on another network"
        message="Switch your wallet to Arbitrum One and we'll read your collection. We'd rather show you nothing than another chain's balances."
      />
    );
  }

  if (chain.ownedStatus === METRIC.LOADING || chain.ownedStatus === METRIC.IDLE) {
    return <CollectionSkeleton />;
  }

  if (chain.ownedStatus === METRIC.ERROR) {
    return (
      <ChainErrorCard
        title="Couldn't read your Supernova collection"
        message={summary.owned.message}
        onRetry={chain.refresh}
        retrying={chain.loading}
      />
    );
  }

  if (summary.isEmptyCollection) {
    return <EmptyCollectionCard stakeStateKnown={summary.stakeStateKnown} />;
  }

  return <SupernovaCollectionList summary={summary} onStake={onStake} />;
};

export default SupernovaTab;
